import { useEffect, useState } from "react";
import { CircleHelp } from "lucide-react";
import {
  DEFAULT_REFRESH_OPTIONS,
  type RefreshMonitorRes,
  type RefreshOptionsReq,
} from "@/services/backend/qpm/catalog/types/catalog";
import {
  useQpmCatalogPublishMutation,
  useQpmCatalogReleaseRefreshMonitorApplyMutation,
  useQpmCatalogReleaseRefreshMonitorPreviewMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import t from "@/lang/lang";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { RefreshPreviewDiff } from "@/routes/base-data/processes/qpm/catalog/_components/refresh-preview-diff";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { toast } from "sonner";

type Props = {
  releaseId?: string;
  monitorId: string;
  catalogId: string;
  onApplied?: () => void;
  currentMonitorSnap: MonitorSnapDTO;
};

function boolLabel(label: string, checked: boolean) {
  return `${label}: ${checked ? t("Ja") : t("Nein")}`;
}

export function CatalogReleaseRefreshControls({
  releaseId,
  monitorId,
  catalogId,
  onApplied,
  currentMonitorSnap,
}: Props) {
  const monitorType = currentMonitorSnap.type;

  const baseOverwriteOptionKeys = [
    "overrideCoreFields",
    "overrideCapabilities",
    "overrideQPMService",
    "overrideDetails",
    "overrideImage",
    "overridePests",
    "overrideTimes",
    "overrideRecommendedVisits",
    "overrideBasePrice",
    "overrideAdditionals",
  ] as const;

  const overwriteOptionKeys = [
    ...baseOverwriteOptionKeys,
    ...(MonitorTypeUtil.supportsRentingDefaults(monitorType)
      ? (["overrideRentingDefaults"] as const)
      : []),
    ...(MonitorTypeUtil.supportsConsumables(monitorType)
      ? (["overrideConsumables", "overrideConsumablePrices"] as const)
      : []),
  ] as const;

  const requiredOverwriteOptions: Partial<RefreshOptionsReq> = {
    overrideCoreFields: true,
    overrideCapabilities: true,
    overrideQPMService: true,
    overrideDetails: true,
    overrideImage: true,
    overridePests: true,
    overrideTimes: false,
    overrideRecommendedVisits: false,
    overrideBasePrice: false,
    overrideAdditionals: true,
    overrideRentingDefaults: false,
    overrideConsumables: false,
    overrideConsumablePrices: false,
  };

  const [options, setOptions] = useState<RefreshOptionsReq>(
    DEFAULT_REFRESH_OPTIONS,
  );
  const [resolvedReleaseId, setResolvedReleaseId] = useState<
    string | undefined
  >(releaseId);
  const [lastPreview, setLastPreview] = useState<RefreshMonitorRes | undefined>(
    undefined,
  );

  const [publish, publishState] = useQpmCatalogPublishMutation();
  const [preview, previewState] =
    useQpmCatalogReleaseRefreshMonitorPreviewMutation();
  const [apply, applyState] = useQpmCatalogReleaseRefreshMonitorApplyMutation();

  useEffect(() => {
    setResolvedReleaseId(releaseId);
  }, [releaseId]);

  const isBusy =
    publishState.isLoading || previewState.isLoading || applyState.isLoading;
  const canRun = !isBusy;
  const overwriteAllSelected = overwriteOptionKeys.every((key) => options[key]);

  useEffect(() => {
    setOptions((prev) => ({
      ...prev,
      overrideRentingDefaults: MonitorTypeUtil.supportsRentingDefaults(
        monitorType,
      )
        ? prev.overrideRentingDefaults
        : false,
      overrideConsumables: MonitorTypeUtil.supportsConsumables(monitorType)
        ? prev.overrideConsumables
        : false,
      overrideConsumablePrices: MonitorTypeUtil.supportsConsumables(monitorType)
        ? prev.overrideConsumablePrices
        : false,
    }));
  }, [monitorType]);

  const setOverwriteOption = (
    key: (typeof overwriteOptionKeys)[number],
    value: boolean,
  ) => {
    setOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setOverwriteAll = (checked: boolean) => {
    setOptions((prev) => {
      if (checked) {
        const all: Partial<RefreshOptionsReq> = {};
        for (const key of overwriteOptionKeys) {
          all[key] = true;
        }
        return {
          ...prev,
          ...all,
        };
      }

      return {
        ...prev,
        ...requiredOverwriteOptions,
      } as RefreshOptionsReq;
    });
  };

  const resolvePublishedReleaseId = (published: unknown): string => {
    const candidate =
      (published as { id?: unknown })?.id ??
      (published as { ID?: unknown })?.ID;
    return typeof candidate === "string" ? candidate : "";
  };

  const ensureReleaseId = async (): Promise<string> => {
    if (resolvedReleaseId) {
      return resolvedReleaseId;
    }
    const published = await publish({ catalogId }).unwrap();
    const publishedReleaseId = resolvePublishedReleaseId(published);
    if (!publishedReleaseId) {
      throw new Error("catalog publish returned no release id");
    }
    setResolvedReleaseId(publishedReleaseId);
    return publishedReleaseId;
  };

  const runPreview = async () => {
    const currentReleaseId = await ensureReleaseId();
    const res = await preview({
      releaseId: currentReleaseId,
      monitorId,
      catalogId,
      options,
    }).unwrap();
    setLastPreview(res);
  };

  const runApply = async () => {
    const currentReleaseId = await ensureReleaseId();
    await apply({
      releaseId: currentReleaseId,
      monitorId,
      catalogId,
      options,
    }).unwrap();
    toast.success(t("Monitor wurde aktualisiert"));
    onApplied?.();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Mit Basis Monitor abgleichen")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {!resolvedReleaseId && (
            <div className="text-muted-foreground text-sm">
              {t(
                "Es ist noch keine Veröffentlichung vorhanden. Beim Ausführen wird automatisch eine Veröffentlichung erstellt.",
              )}
            </div>
          )}

          {(publishState.error || previewState.error || applyState.error) && (
            <RTKQueryErrorAlert
              error={
                (publishState.error ?? previewState.error ?? applyState.error)!
              }
            />
          )}

          <div className="grid gap-2 sm:grid-cols-1 md:grid-cols-2">
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                id={`${monitorId}-refresh-overwrite-all`}
                checked={overwriteAllSelected}
                onCheckedChange={(v) => setOverwriteAll(Boolean(v))}
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-overwrite-all`}
              >
                {boolLabel(t("Alles überschreiben"), overwriteAllSelected)}
              </label>
            </div>

            <div className="flex flex-col gap-1 md:col-span-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${monitorId}-refresh-force`}
                  checked={options.force}
                  onCheckedChange={(v) =>
                    setOptions((prev) => ({ ...prev, force: Boolean(v) }))
                  }
                />
                <label
                  className="text-sm"
                  htmlFor={`${monitorId}-refresh-force`}
                >
                  {boolLabel(t("Update erzwingen"), options.force)}
                </label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center"
                        aria-label={t("Info zu Update erzwingen")}
                      >
                        <CircleHelp className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t(
                          "Erzwingt eine Aktualisierung, auch wenn in der Vorschau keine Änderung angezeigt wird",
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-core`}
                checked={options.overrideCoreFields}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideCoreFields", Boolean(v))
                }
              />
              <label className="text-sm" htmlFor={`${monitorId}-refresh-core`}>
                {boolLabel(t("Standardfelder"), options.overrideCoreFields)}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-capabilities`}
                checked={options.overrideCapabilities}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideCapabilities", Boolean(v))
                }
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-capabilities`}
              >
                {boolLabel(
                  t("Funktionen überschreiben"),
                  options.overrideCapabilities,
                )}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-qpm-service`}
                checked={options.overrideQPMService}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideQPMService", Boolean(v))
                }
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-qpm-service`}
              >
                {boolLabel(
                  t("QPM Service überschreiben"),
                  options.overrideQPMService,
                )}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-details`}
                checked={options.overrideDetails}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideDetails", Boolean(v))
                }
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-details`}
              >
                {boolLabel(t("Details überschreiben"), options.overrideDetails)}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-image`}
                checked={options.overrideImage}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideImage", Boolean(v))
                }
              />
              <label className="text-sm" htmlFor={`${monitorId}-refresh-image`}>
                {boolLabel(t("Bild überschreiben"), options.overrideImage)}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-pests`}
                checked={options.overridePests}
                onCheckedChange={(v) =>
                  setOverwriteOption("overridePests", Boolean(v))
                }
              />
              <label className="text-sm" htmlFor={`${monitorId}-refresh-pests`}>
                {boolLabel(
                  t("Schädlingsdaten überschreiben"),
                  options.overridePests,
                )}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-times`}
                checked={options.overrideTimes}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideTimes", Boolean(v))
                }
              />
              <label className="text-sm" htmlFor={`${monitorId}-refresh-times`}>
                {boolLabel(
                  t("Service Zeiten überschreiben"),
                  options.overrideTimes,
                )}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-visits`}
                checked={options.overrideRecommendedVisits}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideRecommendedVisits", Boolean(v))
                }
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-visits`}
              >
                {boolLabel(
                  t("Anzahl Maßnahmen überschreiben"),
                  options.overrideRecommendedVisits,
                )}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-baseprice`}
                checked={options.overrideBasePrice}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideBasePrice", Boolean(v))
                }
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-baseprice`}
              >
                {boolLabel(t("Preis überschreiben"), options.overrideBasePrice)}
              </label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`${monitorId}-refresh-additionals`}
                checked={options.overrideAdditionals}
                onCheckedChange={(v) =>
                  setOverwriteOption("overrideAdditionals", Boolean(v))
                }
              />
              <label
                className="text-sm"
                htmlFor={`${monitorId}-refresh-additionals`}
              >
                {boolLabel(
                  t("Material-Zusätze überschreiben"),
                  options.overrideAdditionals,
                )}
              </label>
            </div>

            {MonitorTypeUtil.supportsRentingDefaults(monitorType) ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${monitorId}-refresh-renting`}
                  checked={options.overrideRentingDefaults}
                  onCheckedChange={(v) =>
                    setOverwriteOption("overrideRentingDefaults", Boolean(v))
                  }
                />
                <label
                  className="text-sm"
                  htmlFor={`${monitorId}-refresh-renting`}
                >
                  {boolLabel(
                    t("Mietkonditionen überschreiben"),
                    options.overrideRentingDefaults,
                  )}
                </label>
              </div>
            ) : null}
            {MonitorTypeUtil.supportsConsumables(monitorType) ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${monitorId}-refresh-consumables`}
                  checked={options.overrideConsumables}
                  onCheckedChange={(v) =>
                    setOverwriteOption("overrideConsumables", Boolean(v))
                  }
                />
                <label
                  className="text-sm"
                  htmlFor={`${monitorId}-refresh-consumables`}
                >
                  {boolLabel(
                    t("Verbrauchsmaterialien überschreiben"),
                    options.overrideConsumables,
                  )}
                </label>
              </div>
            ) : null}

            {MonitorTypeUtil.supportsConsumables(monitorType) ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${monitorId}-refresh-consumable-prices`}
                  checked={options.overrideConsumablePrices}
                  onCheckedChange={(v) =>
                    setOverwriteOption("overrideConsumablePrices", Boolean(v))
                  }
                />
                <label
                  className="text-sm"
                  htmlFor={`${monitorId}-refresh-consumable-prices`}
                >
                  {boolLabel(
                    t("Verbrauchsmaterialpreise überschreiben"),
                    options.overrideConsumablePrices,
                  )}
                </label>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={runPreview} disabled={!canRun}>
                {t("Vorschau")}
              </Button>
              <Button onClick={runApply} disabled={!canRun}>
                {t("Aktualisieren")}
              </Button>
            </div>

            <div>
              <Button
                variant="ghost"
                onClick={() => setOptions(DEFAULT_REFRESH_OPTIONS)}
                disabled={isBusy}
              >
                {t("Zurücksetzen")}
              </Button>
            </div>
          </div>

          {lastPreview && (
            <div className="rounded-md border p-3">
              <div className="grid gap-1 text-sm">
                {!lastPreview.changed && (
                  <>
                    <div>
                      <span>{t("Keine Änderungen vorhanden. ")}</span>
                    </div>
                    <div>
                      <span>
                        {t(
                          'Sie können mit "Update erzwingen" dennoch die Werte vergleichen oder überschreiben.',
                        )}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {lastPreview.proposedSnapshot && (
                <div className="mt-3">
                  <RefreshPreviewDiff
                    currentMonitorSnap={currentMonitorSnap}
                    proposedSnapshot={lastPreview.proposedSnapshot}
                    oldHash={lastPreview.oldHash}
                    newHash={lastPreview.newHash}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
